import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import './SupervisorUpdatePage.css';
import { toast } from '../components/Toast';

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function DetailModal({ update, onClose, onApprove, onReject, processing }) {
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState({});
  const [photosLoading, setPhotosLoading] = useState(false);

  useEffect(() => {
    if (!update) return;
    setPhotos([]);
    setPhotoUrls({});
    setPhotosLoading(true);
    axios.get(`/supervisor-updates/${update.id}/photos`)
      .then(res => {
        const data = res.data?.data || [];
        setPhotos(Array.isArray(data) ? data : []);
        (Array.isArray(data) ? data : []).forEach(p => {
          axios.get(`/supervisor-updates/${update.id}/photos/${p.id}/download`, { responseType: 'blob' })
            .then(r => {
              const url = URL.createObjectURL(r.data);
              setPhotoUrls(prev => ({ ...prev, [p.id]: url }));
            }).catch(() => {});
        });
      })
      .catch(() => {})
      .finally(() => setPhotosLoading(false));
    return () => { Object.values(photoUrls).forEach(u => URL.revokeObjectURL(u)); };
  }, [update?.id]);

  if (!update) return null;
  return (
    <div className="su-modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="su-modal">
        <div className="su-modal-header">
          <h3>Review Update</h3>
          <button className="su-close" onClick={onClose}>&times;</button>
        </div>
        <div className="su-modal-body">
          <div className="su-detail-grid">
            <div className="su-detail-item">
              <span className="su-detail-label">Submitted By</span>
              <span className="su-detail-value"><strong>{update.createdByName || '—'}</strong></span>
            </div>
            <div className="su-detail-item">
              <span className="su-detail-label">Project</span>
              <span className="su-detail-value">{update.projectName || '—'}</span>
            </div>
            <div className="su-detail-item">
              <span className="su-detail-label">Update Date</span>
              <span className="su-detail-value">{fmt(update.updateDate)}</span>
            </div>
            <div className="su-detail-item">
              <span className="su-detail-label">Work Progress</span>
              <span className="su-detail-value">
                <div className="su-progress-bar">
                  <div className="su-progress-fill" style={{ width: `${update.workProgress}%` }} />
                </div>
                <span>{update.workProgress}%</span>
              </span>
            </div>
          </div>
          <div className="su-detail-section">
            <span className="su-detail-label">Description</span>
            <div className="su-detail-desc">{update.description || <span className="su-muted">No description</span>}</div>
          </div>
          {update.reviewComment && (
            <div className="su-detail-section">
              <span className="su-detail-label">Previous Review Comment</span>
              <div className="su-detail-desc su-comment-existing">{update.reviewComment}</div>
            </div>
          )}
          {/* Photos */}
          <div className="su-detail-section">
            <span className="su-detail-label">Site Photos</span>
            {photosLoading ? (
              <div className="su-muted" style={{ marginTop: 8 }}>Loading photos…</div>
            ) : photos.length === 0 ? (
              <div className="su-muted" style={{ marginTop: 8 }}>No photos attached</div>
            ) : (
              <div className="su-photo-grid">
                {photos.map(p => (
                  <a key={p.id}
                    href={photoUrls[p.id] || '#'}
                    target="_blank"
                    rel="noreferrer"
                    className="su-photo-thumb"
                    title={p.fileName}
                  >
                    {photoUrls[p.id]
                      ? <img src={photoUrls[p.id]} alt={p.fileName} />
                      : <div className="su-photo-placeholder">⏳</div>
                    }
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="su-detail-section">
            <label className="su-detail-label">Add Review Comment (optional)</label>
            <textarea className="su-comment-input" rows={3}
              placeholder="Add a comment for the supervisor…"
              value={comment} onChange={e => setComment(e.target.value)} />
          </div>
        </div>
        <div className="su-modal-footer">
          <button className="su-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="su-reject-btn" onClick={() => onReject(update.id, comment)}
            disabled={processing === update.id}>
            {processing === update.id ? '…' : '✗ Reject'}
          </button>
          <button className="su-approve-btn" onClick={() => onApprove(update.id, comment)}
            disabled={processing === update.id}>
            {processing === update.id ? '…' : '✓ Approve'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SupervisorUpdateApprovals() {
  const [updates, setUpdates]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [processing, setProcessing] = useState(null);
  const [selected, setSelected]     = useState(null);

  useEffect(() => { fetchPending(); }, []);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/supervisor-updates/pending-approval');
      const data = res.data?.data || [];
      setUpdates(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load pending updates');
    } finally { setLoading(false); }
  };

  const handleApprove = async (id, comment) => {
    setProcessing(id);
    try {
      await axios.post(`/supervisor-updates/${id}/approve`, { comment: comment || null });
      flash('Update approved');
      setSelected(null);
      fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to approve');
    } finally { setProcessing(null); }
  };

  const handleReject = async (id, comment) => {
    setProcessing(id);
    try {
      await axios.post(`/supervisor-updates/${id}/reject`, { comment: comment || null });
      flash('Update returned to pending');
      setSelected(null);
      fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to reject');
    } finally { setProcessing(null); }
  };

  const flash = (msg) => { toast.success(msg); };

  return (
    <div className="supervisor-update-page">
      <div className="page-header">
        <div>
          <h1>Supervisor Update Approvals</h1>
          <p style={{ color: '#666', margin: '4px 0 0' }}>Review and approve pending site progress reports</p>
        </div>
        <button className="btn-secondary" onClick={fetchPending}>↻ Refresh</button>
      </div>

      <div className="su-stats">
        <div className="su-stat">
          <span className="su-stat-val su-pending">{updates.length}</span>
          <span className="su-stat-lbl">Pending Approvals</span>
        </div>
      </div>

      {loading ? (
        <div className="su-loading">Loading…</div>
      ) : updates.length === 0 ? (
        <div className="su-empty">✅ No pending updates — all caught up!</div>
      ) : (
        <div className="su-table-wrap">
          <table className="su-table">
            <thead>
              <tr><th>Supervisor</th><th>Project</th><th>Date</th><th>Progress</th><th>Description</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {updates.map(u => (
                <tr key={u.id} className="su-clickable" onClick={() => setSelected(u)}>
                  <td data-label="Supervisor"><strong>{u.createdByName || '—'}</strong></td>
                  <td data-label="Project">{u.projectName || '—'}</td>
                  <td data-label="Date">{fmt(u.updateDate)}</td>
                  <td data-label="Progress">
                    <div className="su-progress-inline">
                      <div className="su-progress-bar-sm">
                        <div className="su-progress-fill-sm" style={{ width: `${u.workProgress}%` }} />
                      </div>
                      <span>{u.workProgress}%</span>
                    </div>
                  </td>
                  <td data-label="Description" className="su-desc-preview">
                    {u.description ? (u.description.length > 60 ? u.description.slice(0, 60) + '…' : u.description) : '—'}
                  </td>
                  <td data-label="Actions" onClick={e => e.stopPropagation()}>
                    <div className="action-buttons su-action-btns">
                      <button className="btn btn-sm btn-primary su-approve-btn" onClick={() => handleApprove(u.id, '')}
                        disabled={processing === u.id} title="Quick approve">
                        {processing === u.id ? '…' : '✓ Approve'}
                      </button>
                      <button className="btn btn-sm btn-secondary su-view-btn" onClick={() => setSelected(u)}>Review</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DetailModal update={selected} onClose={() => setSelected(null)}
        onApprove={handleApprove} onReject={handleReject} processing={processing} />
    </div>
  );
}

export default SupervisorUpdateApprovals;
